import { graphql, type HeadFC } from 'gatsby';
import React, { useEffect, useState } from 'react';

import ErrorMessage from 'src/components/common/error-message';
import type { PageContext } from 'src/components/common/page-context';
import { PageHead } from 'src/components/common/page-head';
import Spinner from 'src/components/common/spinner';
import SuccessfulVerification from 'src/components/common/successful-verification';
import { useQueryStringValue } from 'src/custom-hooks/use-query-string-value';
import { QueryStringKey } from 'src/enums/query-string-key';
import { post } from 'src/helpers/request-helper';

import * as styles from './contact-verification.module.scss';

enum PAGE_STATE {
  LOADING,
  LOADED,
  ERROR,
}

const verifyContactPreferences = (encodedContactId: string) =>
  post(`members/${encodedContactId}/marketing-opt-in-verification/confirm`, null);

export const ContactVerificationLanding: React.FC = () => {
  const encodedContactId = useQueryStringValue(
    QueryStringKey.CONTACT_VERIFICATION_ENCODED_CONTACT_ID
  );

  const [verificationResult, setVerificationResult] = useState<'pending' | 'loaded' | 'error'>(
    'pending'
  );
  const [retryCounter, setRetryCounter] = useState(0);

  useEffect(() => {
    if (!encodedContactId) {
      return;
    }
    let cancelled = false;
    verifyContactPreferences(encodedContactId)
      .then(() => {
        if (!cancelled) setVerificationResult('loaded');
      })
      .catch(() => {
        if (!cancelled) setVerificationResult('error');
      });
    return () => {
      cancelled = true;
    };
  }, [encodedContactId, retryCounter]);

  const onRetryClicked = () => {
    setVerificationResult('pending');
    setRetryCounter((n) => n + 1);
  };

  const pageState: PAGE_STATE = !encodedContactId
    ? PAGE_STATE.ERROR
    : verificationResult === 'loaded'
      ? PAGE_STATE.LOADED
      : verificationResult === 'error'
        ? PAGE_STATE.ERROR
        : PAGE_STATE.LOADING;

  switch (pageState) {
    case PAGE_STATE.ERROR:
      return <ErrorMessage onRetryClicked={onRetryClicked} />;
    case PAGE_STATE.LOADED:
      return (
        <SuccessfulVerification
          titleMessageKey="thankYouForSubscribing"
          subtitleMessageKey="downloadTheMobileAppHere"
        />
      );
    case PAGE_STATE.LOADING:
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
  }
};

const ContactVerification: React.FC = () => <ContactVerificationLanding />;

export default ContactVerification;

export const Head: HeadFC<never, PageContext> = ({ pageContext }) => {
  return <PageHead pageContext={pageContext} seo={{ titleOverride: 'Contact Verification' }} />;
};

export const query = graphql`
  query ContactVerificationPage($globalDirectoryName: String!) {
    ..._GlobalCmsRecordCore
  }
`;
